import React from 'react';
import { Card } from '@/components/ds';
import { formatDate } from '@/lib/utils';
import { Users, ShieldCheck, Briefcase, Layers, CalendarClock, Activity } from 'lucide-react';

/**
 * AgencySnapshot — condensed operational status block at the top of the
 * Agency Command Center. Six business-summary cells.
 */
const Cell = ({ label, value, tone = 'neutral', icon: Icon, hint }) => {
  const tones = {
    neutral: { fg: 'var(--kr-navy-800)', bg: '#f6f7fa' },
    success: { fg: '#15794a', bg: '#e9f6ef' },
    warning: { fg: 'var(--kr-gold-600)', bg: 'var(--kr-gold-50)' },
    info:    { fg: '#1a4e88', bg: '#e6f0fb' },
  };
  const t = tones[tone] || tones.neutral;
  return (
    <div className="flex items-start gap-3">
      {Icon && (
        <div className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0"
             style={{ background: t.bg, color: t.fg }}>
          <Icon size={17} />
        </div>
      )}
      <div className="min-w-0">
        <div className="text-[10px] font-semibold uppercase tracking-wider"
             style={{ color: 'var(--kr-subtle)' }}>{label}</div>
        <div className="text-sm font-semibold mt-0.5 truncate"
             style={{ color: 'var(--kr-navy-900)' }}>{value ?? '—'}</div>
        {hint && <div className="text-[11px] text-slate-500 mt-0.5">{hint}</div>}
      </div>
    </div>
  );
};

export const AgencySnapshot = ({ agency, summary, appointments = [], enabledCarriers = [] }) => {
  const activeAppointments = appointments.filter(
    (a) => a.appointment_status === 'Active' && a.writing_number
  ).length;
  // The two carrier lists represent different concepts:
  //   • appointments = writing numbers actually held by this agency
  //   • enabledCarriers = carriers this agency is allowed to request against
  // We surface the primary agency-level concept (enabled) and note appointments
  // separately in the Active Appointments cell above.
  const enabledCount = enabledCarriers.length;
  const totalAgents = summary?.total_agents ?? 0;
  const directAgents = summary?.direct_agents ?? 0;
  const agentsHint = summary && totalAgents !== directAgents
    ? `${directAgents} direct · ${totalAgents - directAgents} downline`
    : (totalAgents ? 'All direct' : null);
  const lastUpdated = agency?.updated_at || agency?.created_at;

  return (
    <Card testId="agency-snapshot">
      <div className="grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-6 gap-5">
        <Cell label="Total Agents" value={totalAgents} icon={Users}
              tone={totalAgents > 0 ? 'success' : 'neutral'} hint={agentsHint} />
        <Cell label="Active Appointments" value={activeAppointments} icon={ShieldCheck}
              tone={activeAppointments > 0 ? 'success' : 'neutral'}
              hint="Writing numbers held by this agency" />
        <Cell label="Enabled Carriers" value={enabledCount} icon={Briefcase}
              tone={enabledCount > 0 ? 'info' : 'neutral'}
              hint="Carriers this agency may request" />
        <Cell label="Hierarchy Depth" value={agency?.depth ?? '—'} icon={Layers} tone="info"
              hint={agency?.depth === 0 ? 'Root agency' : `${agency?.depth} level${agency?.depth === 1 ? '' : 's'} deep`} />
        <Cell label="Created" value={agency?.created_at ? formatDate(agency.created_at) : '—'} icon={CalendarClock} />
        <Cell label="Last Updated" value={lastUpdated ? formatDate(lastUpdated) : '—'} icon={Activity} tone="info" />
      </div>
    </Card>
  );
};

export default AgencySnapshot;
